import { useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { useCreateExcelFile } from '../../services/mutations';
import { NotificationSeverity, NotificationStatus, NotificationMessage, ErrorMessages } from './constants';

/**
 * ExportToExcelButton component to handle exporting data to an Excel file.
 *
 * @param {{
 *   placeCode: string;
 *   tabName: string;
 *   selectedTime: number;
 *   isDisabled: boolean;
 * }} props
 * @param {string} props.placeCode - The code representing the place (e.g., mill)
 * @param {string} props.tabName - The name of the active tab
 * @param {number} props.selectedTime - The selected time for the data
 * @param {boolean} props.isDisabled - Boolean indicating whether the button should be disabled
 * @returns {*} - The ExportToExcelButton component
 */
const ExportToExcelButton = ({
  placeCode,
  tabName,
  sourceName,
  selectedTime,
  isDisabled,
}: {
  placeCode: string;
  tabName: string;
  sourceName: string;
  selectedTime: number;
  isDisabled: boolean;
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const toast = useRef<Toast>(null);
  const { mutateAsync: createExcelFileAsync } = useCreateExcelFile();

  const handleCreateExcel = async (placeCode: string, tabName: string, sourceName: string, selectedTime: number) => {
    setIsExporting(true);

    toast.current?.show({
      severity: NotificationSeverity.Info,
      summary: NotificationStatus.ExportInitiated,
      detail: NotificationMessage.ExportStarted,
      life: 3000,
    });

    try {
      await createExcelFileAsync({ placeCode, tabName, sourceName, selectedTime });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : ErrorMessages.UnexpectedError;
      setErrorMessage(`Error: ${errorMessage} (Place Code: ${placeCode}, Tab: ${tabName}, Time: ${selectedTime})`);
      setErrorDialogVisible(true);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Button
        icon="pi pi-file-excel"
        label="Export Data"
        size="small"
        tooltip="Export data to Excel (all categories)"
        tooltipOptions={{ position: 'top' }}
        onClick={() => handleCreateExcel(placeCode, tabName, sourceName, selectedTime)}
        disabled={isDisabled || isExporting}
      />
      <Dialog
        header="Export Error"
        visible={errorDialogVisible}
        style={{ width: '50vw' }}
        modal
        onHide={() => setErrorDialogVisible(false)}
        footer={<Button label="Close" icon="pi pi-times" onClick={() => setErrorDialogVisible(false)} />}
      >
        <p>{errorMessage}</p>
      </Dialog>
    </>
  );
};

export default ExportToExcelButton;
